import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { Fragment, useState, type ReactElement } from "react";
import { ContentHeader, Modal, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { FiscalFileLink, PosBootstrap, PosSaleView } from "@cx/contracts";
import { formatBRL, formatDocument, formatQty, toQ4 } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { accessKeyGroups } from "../invoices/labels";
import { POS_METHOD_LABEL, nfceInFlight, nfceLabel } from "./labels";

const when = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    dateStyle: "short",
    timeStyle: "medium",
  });
const idem = () => ({ "idempotency-key": `k-${crypto.randomUUID()}` });

/**
 * Venda do PDV (T3.1): itens, pagamentos, NFC-e (DANFE/XML, reenvio) e as ações de quem administra o PDV —
 * cancelar (NFC-e em até 30 min da autorização; estorno de contas, estoque e caixa) e devolução/troca.
 */
export function PosSalePage(): ReactElement {
  const { id } = useParams({ from: "/vendas/pdv/$id" });
  const { companyId, me, api } = useSession();
  const canManage = can(me.permissions as Permissions, "pos", "update");
  const qc = useQueryClient();
  const toast = useToast();
  const [dialog, setDialog] = useState<null | "cancel" | "return">(null);
  const sale = useQuery({
    queryKey: ["pos-sales", companyId, "view", id],
    queryFn: () => api<PosSaleView>(`/pos/sales/${id}`),
    // NFC-e em envio, ou venda cancelada com a NFC-e ainda autorizada (evento de cancelamento na SEFAZ): acompanha.
    refetchInterval: (q) => {
      const d = q.state.data;
      if (nfceInFlight(d?.nfce ?? null)) return 2000;
      return d?.status === "canceled" && d.nfce?.status === "authorized" && q.state.dataUpdateCount < 60
        ? 3000
        : false;
    },
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["pos-sales", companyId] });
  const resend = useMutation({
    mutationFn: () => api(`/pos/sales/${id}/nfce`, { method: "POST", headers: idem() }),
    onSuccess: () => {
      toast("success", "NFC-e enviada de novo.");
      void refresh();
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const openFile = async (file: "danfe" | "xml") => {
    try {
      const link = await api<FiscalFileLink>(`/pos/sales/${id}/${file}`);
      window.open(link.url, "_blank", "noopener");
    } catch (e) {
      toast("danger", errorMessage(e));
    }
  };
  const s = sale.data;
  const nf = nfceLabel(s?.nfce ?? null);
  return (
    <>
      <ContentHeader
        title={s ? `Venda do PDV nº ${s.number}` : "Venda do PDV"}
        breadcrumb={["Vendas", "Vendas do PDV", s ? `nº ${s.number}` : "…"]}
        actions={
          s && (
            <div className="d-flex flex-wrap gap-2">
              {s.nfce?.status === "authorized" && (
                <>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => void openFile("danfe")}
                  >
                    DANFE NFC-e
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => void openFile("xml")}
                  >
                    XML
                  </button>
                </>
              )}
              {canManage &&
                s.status === "completed" &&
                (s.nfce?.status === "rejected" || s.nfce?.status === "draft") && (
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    disabled={resend.isPending}
                    onClick={() => resend.mutate()}
                  >
                    Reenviar NFC-e
                  </button>
                )}
              {canManage && s.status === "completed" && (
                <>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setDialog("return")}
                  >
                    Devolução / troca
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => setDialog("cancel")}
                  >
                    Cancelar venda
                  </button>
                </>
              )}
            </div>
          )
        }
      />
      <QueryStatus loading={sale.isLoading} error={sale.error} />
      {s && (
        <div className="row g-3">
          <div className="col-lg-8">
            <div className="box card p-3">
              <dl className="row mb-0">
                <dt className="col-sm-3">Situação</dt>
                <dd className="col-sm-9">
                  <StatusBadge tone={s.status === "completed" ? "success" : "secondary"}>
                    {s.status === "completed" ? "Concluída" : "Cancelada"}
                  </StatusBadge>
                  {s.offline && <span className="badge text-bg-warning ms-2">Vendida sem conexão</span>}
                  {s.cancelReason && <div className="small text-muted mt-1">Motivo: {s.cancelReason}</div>}
                </dd>
                <dt className="col-sm-3">Data</dt>
                <dd className="col-sm-9">{when(s.soldAt)}</dd>
                <dt className="col-sm-3">Terminal / caixa</dt>
                <dd className="col-sm-9">
                  {s.terminalName} ·{" "}
                  <Link to="/vendas/pdv/caixas/$id" params={{ id: s.cashSessionId }}>
                    ver caixa
                  </Link>
                </dd>
                <dt className="col-sm-3">Cliente</dt>
                <dd className="col-sm-9">
                  {s.contactName}
                  {s.consumerDocument && (
                    <span className="text-muted ms-2">{formatDocument(s.consumerDocument)}</span>
                  )}
                </dd>
                <dt className="col-sm-3">Pedido</dt>
                <dd className="col-sm-9">
                  <Link to="/vendas/pedidos/$id" params={{ id: s.salesOrderId }}>
                    nº {s.salesOrderNumber}
                  </Link>
                </dd>
              </dl>
            </div>
            <div className="box card mt-3">
              <div className="table-responsive">
                <table className="table align-middle mb-0" aria-label="Itens">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th className="text-end">Qtd</th>
                      <th className="text-end">Unitário</th>
                      <th className="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.items.map((it, k) => (
                      <tr key={k}>
                        <td>
                          {it.description}
                          {it.code && <span className="text-muted small ms-2">{it.code}</span>}
                        </td>
                        <td className="text-end">
                          {formatQty(it.qtyQ4, 3)} {it.unit}
                        </td>
                        <td className="text-end">{formatBRL(it.unitPriceCents)}</td>
                        <td className="text-end">{formatBRL(it.totalCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {s.returns.length > 0 && (
              <div className="box card mt-3 p-3">
                <h2 className="h6">Devoluções</h2>
                <ul className="mb-0">
                  {s.returns.map((r) => (
                    <li key={r.id}>
                      {when(r.createdAt)} — {formatBRL(r.totalCents)} (
                      {r.refundMethod === "cash" ? "dinheiro devolvido" : "troca"}) — {r.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="col-lg-4">
            <div className="box card p-3">
              <dl className="row mb-0">
                <dt className="col-6 fw-normal">Bruto</dt>
                <dd className="col-6 text-end">{formatBRL(s.grossCents)}</dd>
                <dt className="col-6 fw-normal">Desconto</dt>
                <dd className="col-6 text-end">{formatBRL(s.discountCents)}</dd>
                <dt className="col-6">Total</dt>
                <dd className="col-6 text-end fw-semibold">{formatBRL(s.totalCents)}</dd>
                {s.payments.map((x, k) => (
                  <Fragment key={k}>
                    <dt className="col-6 fw-normal">
                      {x.name} <span className="small text-muted">({POS_METHOD_LABEL[x.kind]})</span>
                    </dt>
                    <dd className="col-6 text-end">{formatBRL(x.amountCents)}</dd>
                  </Fragment>
                ))}
                <dt className="col-6 fw-normal">Troco</dt>
                <dd className="col-6 text-end">{formatBRL(s.changeCents)}</dd>
                {s.divergenceCents !== 0 && (
                  <>
                    <dt className="col-6 fw-normal text-danger">Divergência do terminal</dt>
                    <dd className="col-6 text-end text-danger">{formatBRL(s.divergenceCents)}</dd>
                  </>
                )}
                {s.shortfallCents > 0 && (
                  <>
                    <dt className="col-6 fw-normal text-danger">Em aberto (falta)</dt>
                    <dd className="col-6 text-end text-danger">{formatBRL(s.shortfallCents)}</dd>
                  </>
                )}
              </dl>
            </div>
            <div className="box card p-3 mt-3" data-testid="pos-sale-nfce">
              <h2 className="h6">NFC-e</h2>
              <StatusBadge tone={nf.tone}>{nf.text}</StatusBadge>
              {s.nfce?.number && (
                <div className="small mt-2">
                  nº {s.nfce.number} · série {s.nfce.series}
                </div>
              )}
              {s.nfce?.accessKey && (
                <div className="small text-muted text-break">{accessKeyGroups(s.nfce.accessKey)}</div>
              )}
              {s.nfce?.rejectionReason && (
                <div className="small text-danger mt-2">{s.nfce.rejectionReason}</div>
              )}
            </div>
          </div>
        </div>
      )}
      {s && dialog === "cancel" && (
        <CancelDialog
          id={s.id}
          onClose={(done) => {
            setDialog(null);
            if (done) void refresh();
          }}
        />
      )}
      {s && dialog === "return" && (
        <ReturnDialog
          sale={s}
          onClose={(done) => {
            setDialog(null);
            if (done) void refresh();
          }}
        />
      )}
    </>
  );
}

function CancelDialog({ id, onClose }: { id: string; onClose(done: boolean): void }) {
  const { api } = useSession();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const m = useMutation({
    mutationFn: () =>
      api(`/pos/sales/${id}/cancel`, { method: "POST", headers: idem(), body: JSON.stringify({ reason }) }),
    onSuccess: () => {
      toast("success", "Venda cancelada. A NFC-e autorizada segue para o cancelamento na SEFAZ.");
      onClose(true);
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  return (
    <Modal
      open
      title="Cancelar venda"
      onClose={() => onClose(false)}
      footer={
        <button
          type="button"
          className="btn btn-danger"
          disabled={m.isPending || reason.trim().length < 15}
          onClick={() => m.mutate()}
        >
          Cancelar venda
        </button>
      }
    >
      <p className="small text-muted">
        Estorna as contas, o estoque e o dinheiro (sai do seu caixa aberto). A NFC-e só pode ser cancelada em
        até 30 minutos da autorização.
      </p>
      <label className="form-label" htmlFor="pos-cancel-reason">
        Motivo (mínimo 15 caracteres)
      </label>
      <textarea
        id="pos-cancel-reason"
        className="form-control"
        rows={3}
        maxLength={255}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        data-autofocus
      />
    </Modal>
  );
}

function ReturnDialog({ sale, onClose }: { sale: PosSaleView; onClose(done: boolean): void }) {
  const { api, companyId } = useSession();
  const toast = useToast();
  const boot = useQuery({
    queryKey: ["pos-boot", companyId],
    queryFn: () => api<PosBootstrap>("/pos/bootstrap"),
  });
  const products = sale.items.filter((i) => i.productId);
  const [qty, setQty] = useState<Record<string, string>>({});
  const [refund, setRefund] = useState<"cash" | "none">("cash");
  const [reason, setReason] = useState("");
  const session = boot.data?.openSession ?? null;
  let items: { productId: string; qtyQ4: number }[] = [];
  let invalid = false;
  try {
    items = Object.entries(qty)
      .filter(([, v]) => v.trim() !== "")
      .map(([productId, v]) => ({ productId, qtyQ4: toQ4(v) }))
      .filter((x) => x.qtyQ4 > 0);
  } catch {
    invalid = true;
  }
  const m = useMutation({
    mutationFn: () =>
      api(`/pos/sales/${sale.id}/returns`, {
        method: "POST",
        headers: idem(),
        body: JSON.stringify({ cashSessionId: session!.id, items, refund, reason }),
      }),
    onSuccess: () => {
      toast("success", "Devolução registrada.");
      onClose(true);
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  return (
    <Modal
      open
      title="Devolução / troca"
      onClose={() => onClose(false)}
      footer={
        <button
          type="button"
          className="btn btn-primary"
          disabled={m.isPending || !session || invalid || items.length === 0 || reason.trim().length < 3}
          onClick={() => m.mutate()}
        >
          Registrar devolução
        </button>
      }
    >
      {!session && boot.isSuccess && (
        <div className="alert alert-warning small">Abra o seu caixa no PDV: a devolução entra nele.</div>
      )}
      <table className="table table-sm">
        <thead>
          <tr>
            <th>Produto</th>
            <th className="text-end">Vendido</th>
            <th className="text-end">Devolver</th>
          </tr>
        </thead>
        <tbody>
          {products.map((it) => (
            <tr key={it.productId}>
              <td>{it.description}</td>
              <td className="text-end">{formatQty(it.qtyQ4, 3)}</td>
              <td style={{ width: 120 }}>
                <input
                  className="form-control form-control-sm text-end"
                  inputMode="decimal"
                  aria-label={`Quantidade devolvida de ${it.description}`}
                  value={qty[it.productId!] ?? ""}
                  onChange={(e) => setQty((q) => ({ ...q, [it.productId!]: e.target.value }))}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {invalid && <div className="text-danger small">Quantidade inválida.</div>}
      <fieldset className="mb-2">
        <legend className="form-label fs-6">Reembolso</legend>
        <div className="form-check form-check-inline">
          <input
            id="pos-refund-cash"
            className="form-check-input"
            type="radio"
            checked={refund === "cash"}
            onChange={() => setRefund("cash")}
          />
          <label className="form-check-label" htmlFor="pos-refund-cash">
            Dinheiro do caixa
          </label>
        </div>
        <div className="form-check form-check-inline">
          <input
            id="pos-refund-none"
            className="form-check-input"
            type="radio"
            checked={refund === "none"}
            onChange={() => setRefund("none")}
          />
          <label className="form-check-label" htmlFor="pos-refund-none">
            Troca (sem reembolso)
          </label>
        </div>
      </fieldset>
      <label className="form-label" htmlFor="pos-return-reason">
        Motivo
      </label>
      <input
        id="pos-return-reason"
        className="form-control"
        maxLength={255}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
    </Modal>
  );
}
